import {
    useCreateTimeTrialMutation,
    useFetchUserTimeTrialProgressQuery,
} from '@api';
import type {
    TimeTrialProgress as TimeTrialProgressData,
    TimeTrialProgressResponse,
    TimeTrialProgressWithStatus,
} from '@types';
import {getLevelName, mobileCheck} from '@utils';
import {useTranslation} from 'react-i18next';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {OverlayTrigger} from '@goproblems/ui';
import ProgressPopover from '@components/ProgressPopover';
import {useNavigate} from 'react-router';

const kyuLevels1 = [
    '30 kyu',
    '29 kyu',
    '28 kyu',
    '27 kyu',
    '26 kyu',
    '25 kyu',
    '24 kyu',
    '23 kyu',
    '22 kyu',
    '21 kyu',
    '20 kyu',
    '19 kyu',
    '18 kyu',
    '17 kyu',
    '16 kyu',
    '15 kyu',
    '14 kyu',
    '13 kyu',
    '12 kyu',
    '11 kyu',
    '10 kyu',
];

const kyuLevels2 = [
    '9 kyu',
    '8 kyu',
    '7 kyu',
    '6 kyu',
    '5 kyu',
    '4 kyu',
    '3 kyu',
    '2 kyu',
    '1 kyu',
];

const danLevels = ['1 dan', '2 dan', '3 dan', '4 dan', '5 dan', '6 dan'];
const allLevels = [...kyuLevels1, ...kyuLevels2, ...danLevels];

const getStatus = (data: TimeTrialProgressData) => {
    if ((data?.victoriesCount ?? 0) > 0) return 'green';
    if ((data?.trialsCount ?? 0) > 0) return 'gray';
    return 'white';
};

const Box = ({
    level,
    data,
    isNext = false,
    clickable = true,
    showLabel = false,
}: {
    level: string;
    data: TimeTrialProgressWithStatus;
    isNext?: boolean;
    clickable?: boolean;
    showLabel?: boolean;
}) => {
    const {t} = useTranslation();
    const [createTimeTrial] = useCreateTimeTrialMutation();
    const navigate = useNavigate();

    const startTrialFor = async (level: number) => {
        try {
            const res = await createTimeTrial({level}).unwrap();
            if (res.id) navigate(`/time-trials/${res.id}`);
        } catch (error) {
            console.error('Error starting trial', error);
        }
    };

    const scoreLabel =
        data.status === 'green'
            ? t('time_trial_progress.best_time')
            : t('time_trial_progress.max_problems');
    const score =
        data.status === 'green'
            ? data.bestTime
            : data.bestScore || t('time_trial_progress.na');
    const attempts = data.trialsCount || t('time_trial_progress.na');

    return (
        <OverlayTrigger
            placement="top"
            wrapperClassName="time-trial-box-trigger"
            overlay={
                <ProgressPopover
                    title={level}
                    metrics={[
                        {label: scoreLabel, value: score},
                        {
                            label: t('time_trial_progress.attempts'),
                            value: attempts,
                        },
                    ]}
                />
            }>
            <button
                type="button"
                className={`time-trial-box ${data.status}${
                    isNext
                        ? ' is-next ring-1 ring-neutral-400 ring-offset-1 dark:ring-neutral-500 dark:ring-offset-neutral-800'
                        : ''
                }`}
                data-level={level}
                aria-label={clickable ? `${t('index.start')} ${level}` : level}
                style={!clickable ? {cursor: 'default'} : undefined}
                onClick={() => {
                    if (!clickable || !Number.isFinite(data.level)) return;
                    if (!mobileCheck()) {
                        void startTrialFor(data.level);
                    } else {
                        window.location.href = '/time-trials';
                    }
                }}>
                {showLabel && level.replace(' kyu', 'k').replace(' dan', 'd')}
            </button>
        </OverlayTrigger>
    );
};

const HomeProgress = ({
    data,
    clickable,
    variant = 'home',
}: {
    data: TimeTrialProgressResponse;
    clickable: boolean;
    variant?: 'home' | 'profile';
}) => {
    const {t} = useTranslation();
    const levelsWithData = allLevels.map(level => {
        const progress = data.find(d => getLevelName(d.level) === level)!;

        return {
            level,
            data: {
                ...progress,
                status: getStatus(progress),
            } as TimeTrialProgressWithStatus,
        };
    });
    const completedCount = levelsWithData.filter(
        level => level.data.status === 'green'
    ).length;
    const nextLevelIndex = levelsWithData.findIndex(
        level => level.data.status !== 'green'
    );
    const nextLevel =
        nextLevelIndex >= 0 ? levelsWithData[nextLevelIndex].level : null;

    return (
        <div className={`time-trial-progress-${variant}`}>
            <div className="time-trial-progress-home-header">
                <span>
                    {t('levels.progress')}{' '}
                    <strong>
                        {completedCount}/{levelsWithData.length}
                    </strong>
                </span>
                {nextLevel && (
                    <span className="time-trial-progress-home-next">
                        {t('single_problem.next')}: {nextLevel}
                    </span>
                )}
            </div>
            <div className={`time-trial-widget time-trial-widget--${variant}`}>
                {levelsWithData.map(({level, data}, index) => (
                    <Box
                        key={level}
                        level={level}
                        data={data}
                        isNext={index === nextLevelIndex}
                        clickable={clickable}
                        showLabel={variant === 'profile'}
                    />
                ))}
            </div>
        </div>
    );
};

const Line = ({
    levels,
    data,
    clickable,
}: {
    levels: string[];
    data: TimeTrialProgressResponse;
    clickable: boolean;
}) => {
    return (
        <div className="time-trial-line">
            {levels.map(level => {
                const levelData: TimeTrialProgressWithStatus = {
                    ...data.find(d => getLevelName(d.level) === level)!,
                    status: getStatus(
                        data.find(d => getLevelName(d.level) === level)!
                    ),
                };

                return (
                    <Box
                        key={level}
                        level={level}
                        data={levelData}
                        clickable={clickable}
                    />
                );
            })}
        </div>
    );
};

export const TimeTrialProgressView = ({
    data: timeTrialProgressData,
    isLoading,
    isError,
    variant = 'default',
    clickable = true,
}: {
    data?: TimeTrialProgressResponse;
    isLoading: boolean;
    isError: boolean;
    variant?: 'default' | 'home' | 'profile';
    clickable?: boolean;
}) => {
    const {t} = useTranslation();

    if (isLoading)
        return (
            <div
                className={
                    variant === 'home'
                        ? 'time-trial-progress-home-loading'
                        : 'm-1 flex justify-center align-middle'
                }>
                {variant === 'home' && (
                    <span className="index-home-progress-label">
                        {t('levels.progress')}
                    </span>
                )}
                {variant === 'home' ? (
                    <div
                        className="time-trial-widget time-trial-widget--home"
                        aria-hidden="true">
                        {allLevels.map(level => (
                            <div key={level} className="relative aspect-square">
                                <ThemeAwareSkeleton
                                    containerClassName="absolute inset-0"
                                    className="!h-full !rounded-sm"
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <ThemeAwareSkeleton containerClassName="flex-1" count={2} />
                )}
            </div>
        );
    if (isError || timeTrialProgressData === undefined) return null;

    if (variant === 'home' || variant === 'profile') {
        return (
            <HomeProgress
                data={timeTrialProgressData}
                clickable={clickable}
                variant={variant}
            />
        );
    }

    return (
        <div className="time-trial-widget">
            <Line
                levels={kyuLevels1}
                data={timeTrialProgressData}
                clickable={clickable}
            />
            <Line
                levels={kyuLevels2}
                data={timeTrialProgressData}
                clickable={clickable}
            />
            <Line
                levels={danLevels}
                data={timeTrialProgressData}
                clickable={clickable}
            />
        </div>
    );
};

const TimeTrialProgress = ({
    userId,
    variant = 'default',
    clickable = true,
}: {
    userId: number;
    variant?: 'default' | 'home' | 'profile';
    clickable?: boolean;
}) => {
    const {data, isLoading, isError} =
        useFetchUserTimeTrialProgressQuery(userId);

    return (
        <TimeTrialProgressView
            data={data}
            isLoading={isLoading}
            isError={isError}
            variant={variant}
            clickable={clickable}
        />
    );
};

export default TimeTrialProgress;
